/**
 * Proactive Version Check (bundle-hash compare)
 *
 * Polls /version.json to detect new deploys and notify the user via the
 * UpdateBanner component.
 *
 * Mechanism: compare the loaded entry bundle filename (extracted from
 * `<script src>` in the DOM at page load) against the server's currently
 * advertised entry bundle (from /version.json). If they differ, a newer
 * deploy is live and the user should reload.
 *
 * Why bundle-hash compare instead of build-string compare:
 * - Hashed asset filenames are derived from content, independent of the
 *   embedded VITE_BUILD_VERSION (which can drift if CI rebuilds with a
 *   stale env var).
 * - The loaded bundle filename is what the browser ACTUALLY ran — not
 *   what some embedded string claims it ran. Self-evidence beats
 *   self-report.
 * - When a deploy ships, the new entry chunk gets a new hash (Vite
 *   determinism), and the comparison goes positive immediately on the
 *   next poll cycle.
 *
 * Falls back to build-string compare for backward compatibility if the
 * server's version.json doesn't yet have an `entry` field (e.g. during
 * the deploy that ships this code itself, before the new version.json
 * lands in the bucket).
 *
 * Replaces the prior embedded-VITE_BUILD_VERSION-vs-server-version pattern
 * (2026-05-11, board task X0hbP936TucrLtlzMGA3).
 *
 * Only runs in production — dev server uses HMR instead.
 */

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const EVENT_NAME = 'app-update-available';

let pollTimer: ReturnType<typeof setInterval> | null = null;
let updateAvailable = false;

interface VersionResponse {
  version?: string;
  entry?: string;
  buildTime?: string;
}

/**
 * Extract the entry bundle filename from the DOM.
 *
 * Vite emits `<script type="module" crossorigin src="/assets/index-<hash>.js">`
 * at the bottom of <head> for the entry chunk. Returns just the basename
 * (e.g. `index-abc123.js`) for stable comparison.
 *
 * Returns null if no matching script is found (dev mode, prerender SSR
 * placeholder, or unexpected DOM shape — caller should treat as "can't
 * determine, skip this check").
 */
function getLoadedEntryBundle(): string | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[src*="/assets/index-"][src$=".js"]'
  );
  for (const script of scripts) {
    const match = script.src.match(/\/(index-[A-Za-z0-9_-]+\.js)(?:\?.*)?$/);
    if (match) return match[1];
  }
  return null;
}

/**
 * Fetch the current server version manifest.
 */
async function fetchServerManifest(): Promise<VersionResponse | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as VersionResponse;
  } catch {
    // Network error, offline, etc. — silently skip this cycle
    return null;
  }
}

/**
 * Check if an update is available and dispatch event if so.
 */
async function checkForUpdate(): Promise<void> {
  if (updateAvailable) return; // Already notified, stop checking

  const manifest = await fetchServerManifest();
  if (!manifest) return; // Couldn't reach server

  const loadedEntry = getLoadedEntryBundle();
  const embeddedVersion = import.meta.env.VITE_BUILD_VERSION;

  // Primary path: bundle-hash compare. Server publishes current entry
  // chunk, client compares against the actually-loaded entry chunk.
  if (manifest.entry && loadedEntry) {
    if (manifest.entry !== loadedEntry) {
      fireUpdateEvent({ loadedEntry, serverEntry: manifest.entry });
    }
    return;
  }

  // Fallback path: build-string compare. Used during the deploy that
  // first ships this code (before the new version.json with `entry`
  // lands), and as a safety net if DOM extraction failed.
  if (manifest.version && embeddedVersion && manifest.version !== embeddedVersion) {
    fireUpdateEvent({
      currentVersion: embeddedVersion,
      serverVersion: manifest.version,
    });
  }
}

/**
 * Fire the update-available event and stop polling.
 */
function fireUpdateEvent(detail: Record<string, unknown>): void {
  updateAvailable = true;
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));

  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/**
 * Initialize the version check poller.
 * Call once on app startup (main.tsx).
 */
export function initVersionCheck(): void {
  // Only run in production
  if (!import.meta.env.PROD) return;

  // Start polling after initial delay (don't check immediately on page load)
  const initialDelay = 30 * 1000; // 30 seconds after load
  setTimeout(() => {
    checkForUpdate();
    pollTimer = setInterval(checkForUpdate, POLL_INTERVAL);
  }, initialDelay);
}

/**
 * Event name for listening to update notifications.
 */
export const UPDATE_EVENT = EVENT_NAME;

/**
 * Check if an update has already been detected.
 */
export function isUpdateAvailable(): boolean {
  return updateAvailable;
}
